import { createContext, ReactNode, useContext } from "react";
import {
  useQuery,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { ApiUser, ApiStaffUser } from "@shared/schema";
import { getQueryFn, apiRequest, queryClient, API_BASE_URL, expireSession } from "../lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { decodeJwtClaims } from "@/lib/jwt";

type AuthContextType = {
  user: ApiUser | null;
  staffUser: ApiStaffUser | null;
  isLoading: boolean;
  error: Error | null;
  isImpersonating: boolean;
  isStaffPickingSidekick: boolean;
  /** Whether this staff user holds the `impersonation` grant. UX only — the
   * API gates every impersonation route independently. */
  canImpersonate: boolean;
  canImpersonateLoading: boolean;
  /** The grant could not be read (server or network failure), so it is unknown
   * rather than absent. An expired session never lands here: it ends instead. */
  canImpersonateError: boolean;
  retryCanImpersonate: () => void;
  loginMutation: UseMutationResult<LoginOutcome, Error, LoginData>;
  logoutMutation: UseMutationResult<null, Error, void>;
  impersonateMutation: UseMutationResult<ApiUser, Error, string>;
  exitImpersonationMutation: UseMutationResult<null, Error, void>;
};

type LoginData = {
  username: string;
  password: string;
};

type PaLoginResponse = {
  access_token: string;
  token_type: string;
  user: ApiUser;
};

type StaffLoginResponse = {
  access_token: string;
  token_type: string;
  user: ApiStaffUser;
};

type LoginOutcome =
  | { kind: "pa"; user: ApiUser }
  | { kind: "staff"; staffUser: ApiStaffUser };

export const AuthContext = createContext<AuthContextType | null>(null);

const TOKEN_KEY = "access_token";
const USER_KEY = "user_data";
const STAFF_TOKEN_KEY = "staff_access_token";
const STAFF_USER_KEY = "staff_user_data";

function readCachedJson<T>(key: string): T | null {
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    localStorage.removeItem(key);
    return null;
  }
}

function readStaffToken(): string | null {
  return localStorage.getItem(STAFF_TOKEN_KEY) ?? localStorage.getItem(TOKEN_KEY);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { toast } = useToast();

  const {
    data: user,
    error,
    isLoading,
  } = useQuery<ApiUser | null, Error>({
    queryKey: ["user"],
    queryFn: async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) return null;

      // Mid-staff-picking refresh: the active token IS the staff token, which
      // would 401 against /auth/me. Skip the call so we don't clear it.
      const staffToken = localStorage.getItem(STAFF_TOKEN_KEY);
      if (staffToken && staffToken === token) return null;

      const cachedUser = readCachedJson<ApiUser>(USER_KEY);
      if (cachedUser) return cachedUser;

      try {
        const res = await fetch(`${API_BASE_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          return null;
        }
        if (!res.ok) return null;
        const data: ApiUser = await res.json();
        localStorage.setItem(USER_KEY, JSON.stringify(data));
        return data;
      } catch {
        return null;
      }
    },
  });

  const { data: staffUser } = useQuery<ApiStaffUser | null, Error>({
    queryKey: ["staffUser"],
    queryFn: async () => readCachedJson<ApiStaffUser>(STAFF_USER_KEY),
  });

  const activeToken = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  const activeClaims = decodeJwtClaims<{ sub?: string; impersonated_by?: string }>(activeToken);
  const isImpersonating = Boolean(user && activeClaims?.impersonated_by);
  const isStaffPickingSidekick = Boolean(staffUser && !user);

  // Impersonation is a per-staff-member grant (staff_feature_access), not a
  // side effect of being staff. Read it so the picker can say "you don't have
  // this" instead of listing sidekicks that all 403 on click. Enforcement lives
  // on the API routes; this is presentation.
  const permissionsQuery = useQuery<Record<string, boolean>, Error>({
    queryKey: ["staffPermissions"],
    // Wait for the user query: while it loads, a live impersonation session looks
    // like a staff user in the picker, and its staff token may already have
    // expired. A 401 here would end a session that is still valid.
    enabled: isStaffPickingSidekick && !isLoading,
    queryFn: async () => {
      const token = readStaffToken();
      const res = await fetch(`${API_BASE_URL}/staff/permissions/me`, {
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
      });
      if (res.status === 401) {
        // Skip if a new login replaced this token while the request was in flight.
        if (readStaffToken() === token) expireSession();
        throw new Error("401: Unauthorized");
      }
      if (!res.ok) throw new Error(`${res.status}: Could not read staff permissions`);
      const body: { permissions?: Record<string, boolean> } = await res.json();
      return body.permissions ?? {};
    },
  });
  const canImpersonate = permissionsQuery.data?.impersonation === true;

  const loginMutation = useMutation<LoginOutcome, Error, LoginData>({
    mutationFn: async (credentials) => {
      const formData = new URLSearchParams();
      formData.append("grant_type", "password");
      formData.append("username", credentials.username);
      formData.append("password", credentials.password);
      formData.append("scope", "");
      formData.append("client_id", "string");
      formData.append("client_secret", "string");

      const paRes = await fetch(`${API_BASE_URL}/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Accept: "application/json",
        },
        body: formData.toString(),
      });

      if (paRes.ok) {
        const tokenData: PaLoginResponse = await paRes.json();
        localStorage.setItem(TOKEN_KEY, tokenData.access_token);
        localStorage.setItem(USER_KEY, JSON.stringify(tokenData.user));
        localStorage.removeItem(STAFF_TOKEN_KEY);
        localStorage.removeItem(STAFF_USER_KEY);
        return { kind: "pa", user: tokenData.user };
      }

      if (paRes.status !== 401) {
        const text = await paRes.text();
        throw new Error(text || "Login failed");
      }

      // Fall back to staff login
      const staffRes = await fetch(`${API_BASE_URL}/staff/auth/login/json`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ email: credentials.username, password: credentials.password }),
      });

      if (!staffRes.ok) {
        const text = await staffRes.text();
        throw new Error(text || "Login failed");
      }

      const staffData: StaffLoginResponse = await staffRes.json();
      // Use the staff token as the active access_token while in the picker
      // state — it grants access to /staff/crm/sidekick-profiles and
      // /staff/impersonate/*. Keep a copy under STAFF_* so we can restore it
      // after "Switch sidekick".
      localStorage.setItem(TOKEN_KEY, staffData.access_token);
      localStorage.setItem(STAFF_TOKEN_KEY, staffData.access_token);
      localStorage.setItem(STAFF_USER_KEY, JSON.stringify(staffData.user));
      localStorage.removeItem(USER_KEY);
      return { kind: "staff", staffUser: staffData.user };
    },
    onSuccess: (outcome) => {
      queryClient.removeQueries({ queryKey: ["staffPermissions"] });
      if (outcome.kind === "pa") {
        queryClient.setQueryData(["user"], outcome.user);
        queryClient.setQueryData(["staffUser"], null);
        toast({
          title: "Login successful",
          description: `Welcome back, ${outcome.user.name ?? ""}`.trim(),
        });
        apiRequest("POST", "/platform_usage/record_event", {
          event: "login",
          detail: null,
        }).catch((e) => console.error("Failed to record login event:", e));
      } else {
        queryClient.setQueryData(["user"], null);
        queryClient.setQueryData(["staffUser"], outcome.staffUser);
        toast({
          title: "Staff login",
          description: "Choose a sidekick to continue.",
        });
      }
    },
    onError: (error) => {
      toast({
        title: "Login failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const logoutMutation = useMutation<null, Error, void>({
    mutationFn: async () => {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem(STAFF_TOKEN_KEY);
      localStorage.removeItem(STAFF_USER_KEY);
      return null;
    },
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ["staffPermissions"] });
      queryClient.setQueryData(["user"], null);
      queryClient.setQueryData(["staffUser"], null);
      toast({
        title: "Logged out",
        description: "You have been successfully logged out",
      });
    },
    onError: (error) => {
      toast({
        title: "Logout failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const impersonateMutation = useMutation<ApiUser, Error, string>({
    mutationFn: async (sidekickUid) => {
      const staffToken = localStorage.getItem(STAFF_TOKEN_KEY);
      if (!staffToken) throw new Error("No staff session — please sign in again.");

      const res = await fetch(
        `${API_BASE_URL}/staff/impersonate/${encodeURIComponent(sidekickUid)}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${staffToken}` },
        },
      );
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Failed to impersonate sidekick");
      }
      const data: PaLoginResponse = await res.json();
      localStorage.setItem(TOKEN_KEY, data.access_token);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      return data.user;
    },
    onSuccess: (impersonatedUser) => {
      queryClient.setQueryData(["user"], impersonatedUser);
      toast({
        title: "Acting as sidekick",
        description: `You are now signed in as ${impersonatedUser.name ?? impersonatedUser.email}.`,
      });
    },
    onError: (error) => {
      toast({
        title: "Could not switch sidekick",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const exitImpersonationMutation = useMutation<null, Error, void>({
    mutationFn: async () => {
      const staffToken = localStorage.getItem(STAFF_TOKEN_KEY);
      if (staffToken) {
        // Restore the staff token as the active token so the picker has access.
        localStorage.setItem(TOKEN_KEY, staffToken);
      } else {
        localStorage.removeItem(TOKEN_KEY);
      }
      localStorage.removeItem(USER_KEY);
      return null;
    },
    onSuccess: () => {
      queryClient.setQueryData(["user"], null);
    },
  });

  return (
    <AuthContext.Provider
      value={{
        user: user ?? null,
        staffUser: staffUser ?? null,
        isLoading,
        error,
        isImpersonating,
        isStaffPickingSidekick,
        canImpersonate,
        canImpersonateLoading:
          isStaffPickingSidekick && (isLoading || permissionsQuery.isLoading),
        canImpersonateError: isStaffPickingSidekick && permissionsQuery.isLoadingError,
        retryCanImpersonate: () => {
          void permissionsQuery.refetch();
        },
        loginMutation,
        logoutMutation,
        impersonateMutation,
        exitImpersonationMutation,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
